import { useEffect, useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faBolt } from "@fortawesome/pro-thin-svg-icons";

import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { Feature } from "common/types";
import { WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";

import Error404Page from "client/common/components/errors/Error404Page";
import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Header from "client/common/components/header/Header";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import PillButton from "client/common/components/buttons/PillButton";
import { useFeatures } from "client/clinics/hooks";
import { ProductListSource } from "client/analytics/types";
import { useProductsViewOnVisible, useViewItemListOnVisible } from "client/analytics/utils";
import { Paths } from "client/routes";

import { useProductDealsFilterOptionState, useProductDealsSearchQuery } from "../hooks";
import { getProductFiltersFromSearch, getSearchString } from "../utils";
import { ProductDealsSearch } from "../types";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import ProductSearchFilterTags from "./ProductSearchFilterTags";
import ProductSearchResult from "./ProductSearchResult";

import styles from "../styles/DealsPage.module.less";

const initialSearch = {
  category_ids: [],
  manufacturer_ids: [],
  q: "",
  page: 1,
  page_size: 20,
  prescription_required: [],
  sort: "",
  species: [],
};

const getEmptyResultsDescription = (query: string) => {
  if (query) {
    return `Could not find any deals for "${query}".`;
  }

  return "Deals change often. Check back soon for new eligible savings.";
};

type DealsResultsProps = {
  search: WithStringValues<ProductDealsSearch>;
  onUpdateSearch: (search: Partial<ProductDealsSearch>) => void;
};

const DealsResults = ({ search, onUpdateSearch }: DealsResultsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);
  const { data: products, isLoading, error, isRefetching } = useProductDealsSearchQuery(search);
  const { data: filterOptions } = useProductDealsFilterOptionState();
  const itemListRef = useViewItemListOnVisible(products?.results, ProductListSource.DealsPage);
  const productsViewRef = useProductsViewOnVisible(products?.results, ProductListSource.DealsPage);

  if (isLoading) {
    return <Results.Loading description="Loading deals" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="deal" />;
  }

  const filters = getProductFiltersFromSearch(search);

  return (
    <div className={styles.content}>
      <div className={styles.info}>
        <div className={styles.label}>
          {isRefetching ? <Spinner /> : getSearchString(products.paginator, "deal")}
        </div>
        <div className={styles.actions}>
          {filterOptions && (
            <ProductSearchFilterTags
              options={filterOptions.filters}
              value={filters}
              onChange={onUpdateSearch}
            />
          )}
          <PillButton
            variant="secondary"
            size="s"
            type="button"
            onClick={() => setIsFiltering(true)}
            disabled={!filterOptions}
          >
            <span className={styles.filter}>
              <Icon icon={faFilter} /> Filter
            </span>
          </PillButton>
        </div>
      </div>
      {filterOptions && (
        <ProductSearchFilterModal
          options={filterOptions.filters}
          isVisible={isFiltering}
          onClose={() => setIsFiltering(false)}
          value={filters}
          onChange={onUpdateSearch}
        />
      )}
      {products.results.length ? (
        <div ref={itemListRef} className={styles.search}>
          <div ref={productsViewRef} className={styles.results}>
            {products.results.map((result) => (
              <ProductSearchResult
                key={result.id}
                result={result}
                listSource={ProductListSource.DealsPage}
              />
            ))}
          </div>
          <Pagination
            paginator={products.paginator}
            onPageChange={(page) => onUpdateSearch({ page })}
            entityName="deal"
          />
        </div>
      ) : (
        <Results.None header="No deals found" description={getEmptyResultsDescription(search.q)} />
      )}
    </div>
  );
};

const DealsPage = () => {
  const [search, updateSearch] = useListSearchState<Paths["products"]["deals"]>(initialSearch);
  const features = useFeatures();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (!features[Feature.Deals]) {
    return <Error404Page />;
  }

  const handleSearch = (q: string) => {
    updateSearch({ q, page: 1 });
  };

  return (
    <Page
      Header={<Header Search={<Header.Search value={search.q} onSearch={handleSearch} />} />}
      className={styles.page}
    >
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Deals</h1>
              <div className={styles.description}>
                Every active discount across the shop — autoship savings, manufacturer rebates, and
                limited-time offers, all in one place.
              </div>
            </div>
            <Icon className={styles.icon} icon={faBolt} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <DealsResults search={search} onUpdateSearch={updateSearch} />
          </Body>
        </div>
      </div>
    </Page>
  );
};

export default DealsPage;
